import './CheckboxComponent.scss';
import { useState, useEffect, useCallback, FC, useRef } from 'react';
import { CheckBoxState } from './model';
import { FilterLevel, FilterType, FilterVM } from '../../../common/view-model';
import { useSelector } from 'react-redux';
import { FilterActions } from '../../../redux/FilterActions';
import { RootState } from '../../../redux/AppReducer';
import { useWindowSize } from '../../../hooks/useWindowSize';
import { v4 as uuidv4 } from 'uuid';
import { publicProductDiscoveryActions } from '../../../common/constants';
import { useAppDispatch } from '../../../redux/store';
import LabelForms from '../../forms/GenericBaseForms/LabelForms/LabelForms';
import { toTitleCase } from '../../../helpers/formatTextTitleCase';
import { buildCategorySelectionPlan } from '../../filter/category-filter/categoryFilterSelection';

export interface ICheckboxComponentProps {
	checked?: boolean;
	label?: string;
	children?: any[];
	parentIndex?: string;
	index?: number;
	style?: string;
	level?: FilterLevel;
	type?: FilterType;
	parentId?: string;
	parentName?: string;
	parentLength: number;
	mainMenuId?: string;
	checkBoxGroupId?: string;
	categoryName?: string;
	carrotIconRef?: string;
	isSuperCategory?: boolean;
	breadcrumText?: string;
	checkBoxDOMRef?: string;
	isLocked?: boolean;
	setLockModalContent?: () => void;
	registerUserClickEvent?: (event: string) => void;
	pendingFilters?: FilterVM[];
	setPendingFilters?: React.Dispatch<React.SetStateAction<FilterVM[]>>;
	isNewFilter?: boolean;
	toolTipText?: React.ReactElement;
	hideTooltip?: boolean;
	isDropdownFilter?: boolean;
	isFirstLevelFilter?: boolean;
	isLowerLevelFilter?: boolean;
	isThirdLevelFilter?: boolean;
	isNonCategoryFilter?: boolean;
}
export const CheckboxComponent: FC<ICheckboxComponentProps> = ({
	checked,
	label,
	children,
	parentIndex,
	index,
	style,
	level,
	type,
	parentId,
	parentName,
	parentLength,
	mainMenuId,
	checkBoxGroupId,
	categoryName,
	carrotIconRef,
	isSuperCategory,
	breadcrumText,
	checkBoxDOMRef = `checklevel${uuidv4()}`,
	isLocked,
	setLockModalContent,
	registerUserClickEvent,
	pendingFilters,
	setPendingFilters,
	isNewFilter,
	toolTipText,
	hideTooltip,
	isDropdownFilter,
	isFirstLevelFilter,
	isLowerLevelFilter,
	isThirdLevelFilter,
	isNonCategoryFilter
}) => {
	const dispatch = useAppDispatch();
	const checkBoxContainerRef = useRef<HTMLDivElement>(null);
	const FULL_SIZE_SCREEN_WIDTH = 768;
	const { width } = useWindowSize();
	const isMobile = width < FULL_SIZE_SCREEN_WIDTH;
	const selectedFilters = useSelector(
		(state: RootState) => state.appState.filterState?.selectedFilters
	);
	const [state, setState] = useState({
		...CheckBoxState,
		checkProperties: {
			checked,
			label,
			children,
			parentIndex,
			index,
			style,
			level,
			type,
			parentId,
			parentLength,
			mainMenuId,
			checkBoxGroupId,
			categoryName,
			carrotIconRef,
			isSuperCategory,
			breadcrumText,
			checkBoxDOMRef
		}
	});
	const addFilterStateCallback = useCallback(
		() => dispatch(FilterActions.addFilterState({ parentIndex, state, setState })),
		[dispatch]
	);
	const checkedStore =
		selectedFilters?.some(filter => filter.id === parentIndex && filter.name === label) ?? false;

	const checkedFilters = isMobile
		? pendingFilters?.find(filter => filter.id === parentIndex && filter.name === label)
				?.isChecked ?? checkedStore
		: checkedStore;
	const createFilter = (isCheck: boolean): FilterVM => ({
		name: label,
		id: parentIndex,
		isChecked: isCheck,
		children,
		level,
		index,
		type,
		state,
		setState,
		parentId,
		parentLength,
		mainMenuId,
		checkBoxGroupId,
		categoryName,
		carrotIconRef,
		isSuperCategory,
		breadcrumText,
		checkBoxDOMRef
	});
	const getActiveFilters = (): FilterVM[] => {
		if (isMobile && pendingFilters) {
			return pendingFilters;
		}

		return selectedFilters ?? [];
	};
	const applySelectionToState = (actions: Array<{ type: 'add' | 'remove'; filter: FilterVM }>) => {
		if (isMobile && setPendingFilters) {
			setPendingFilters(prev => {
				let next = [...prev];
				actions.forEach(action => {
					const existingIndex = next.findIndex(
						item => item.id === action.filter.id && item.name === action.filter.name
					);
					if (action.type === 'add') {
						if (existingIndex >= 0) {
							next[existingIndex] = { ...next[existingIndex], ...action.filter };
						} else {
							next = [...next, action.filter];
						}
					} else if (existingIndex >= 0) {
						next.splice(existingIndex, 1);
					}
				});
				return next;
			});
			return;
		}

		actions.forEach(action => {
			if (action.type === 'add') {
				dispatch(FilterActions.addFilter(action.filter));
			} else {
				dispatch(FilterActions.removeFilter(action.filter, isMobile));
			}
		});
	};
	const updatePendingFilter = (filter: FilterVM) => {
		if (!setPendingFilters) return;

		setPendingFilters(prev => {
			const existingIndex = prev.findIndex(f => f.id === filter.id);

			if (existingIndex !== -1) {
				const updated = [...prev];
				updated[existingIndex] = filter;
				return updated;
			}

			return [...prev, filter];
		});
	};
	const handleLockedFilter = () => {
		if (!isLocked) return false;

		setLockModalContent?.();
		registerUserClickEvent?.(publicProductDiscoveryActions.CLICK_FILTER_CHECKBOX);

		return true;
	};

	const processFilterChange = (filter: FilterVM, isCheck: boolean) => {
		if (level === FilterLevel.SECOND || level === FilterLevel.THIRD) {
			const plan = buildCategorySelectionPlan({
				filter,
				isCheck,
				selectedFilters: getActiveFilters(),
				parentLength,
				parentName
			});
			applySelectionToState(
				plan.map(action => ({
					...action,
					filter: {
						...filter,
						...action.filter,
						name: action.filter.name ?? filter.name,
						id: action.filter.id ?? filter.id,
						parentId: action.filter.parentId ?? filter.parentId,
						parentLength: action.filter.parentLength ?? filter.parentLength,
						state,
						setState,
						isAutoSelected: action.filter.isAutoSelected ?? false
					}
				}))
			);
			return;
		}

		if (isMobile && setPendingFilters) {
			updatePendingFilter(filter);
			return;
		}

		if (isCheck) {
			dispatch(FilterActions.addFilter(filter));
			return;
		}

		dispatch(FilterActions.removeFilter(filter, isMobile));
	};

	const handleDispatch = (event: any) => {
		if (handleLockedFilter()) return;

		const isCheck = event.target.checked;
		processFilterChange(createFilter(isCheck), isCheck);
	};

	const validateIndeterminate = useCallback(() => {
		if (level !== FilterLevel.THIRD) return false;

		const hasParent = selectedFilters?.some(x => x.id === parentIndex);

		const selectedChildren =
			selectedFilters?.filter(x => x.parentId === parentIndex && x.id !== parentIndex).length ?? 0;

		return selectedChildren > 0 && selectedChildren < parentLength && !hasParent;
	}, [selectedFilters, parentIndex, parentLength, level]);
	const checkedIcon = 'ri-checkbox-circle-fill';
	const uncheckedIcon = 'ri-checkbox-blank-circle-line';
	const indeterminateIcon = 'ri-indeterminate-circle-fill';
	const getIconClass = () => {
		if (validateIndeterminate()) {
			return indeterminateIcon;
		}

		return checkedFilters ? checkedIcon : uncheckedIcon;
	};

	useEffect(() => {
		addFilterStateCallback();
	}, []);
	useEffect(() => {
		if (validateIndeterminate()) {
			const indeterminateElement = document.getElementById(checkBoxDOMRef) as HTMLInputElement;
			indeterminateElement.indeterminate = true;
		} else {
			const indeterminateElement = document.getElementById(checkBoxDOMRef) as HTMLInputElement;
			indeterminateElement.indeterminate = false;
		}
	}, [selectedFilters]);

	useEffect(() => {
		if (!checkBoxContainerRef.current) return;

		const element = checkBoxContainerRef.current;

		const updateIcon = () => {
			setIconClass(getIconClass());
		};

		updateIcon();

		element.addEventListener('mouseover', updateIcon);
		element.addEventListener('mouseleave', updateIcon);

		return () => {
			element.removeEventListener('mouseover', updateIcon);
			element.removeEventListener('mouseleave', updateIcon);
		};
	}, [checkedFilters, validateIndeterminate]);
	useEffect(() => {
		if (!isMobile) return;
		const match = pendingFilters?.find(f => f.id === parentIndex && f.name === label);
		const isChecked =
			match?.isChecked ?? selectedFilters?.some(f => f.id === parentIndex && f.name === label);
		setState(prev => ({
			...prev,
			checkProperties: {
				...prev.checkProperties,
				checked: isChecked ?? false
			}
		}));
	}, [pendingFilters, selectedFilters, isMobile, parentIndex, label]);
	const [iconClass, setIconClass] = useState<string>('ri-checkbox-blank-circle-line');
	const toggleCheckbox = () => {
		handleDispatch({
			target: {
				checked: !checkedFilters
			}
		});
	};
	const renderNewFilter = () => (
		<div
			className={`filter-item ${checkedFilters ? 'active' : ''}`}
			ref={checkBoxContainerRef}
			role='button'
			tabIndex={0}
			onClick={toggleCheckbox}
			onKeyDown={e => {
				if (e.key === 'Enter' || e.key === ' ') {
					toggleCheckbox();
				}
			}}
		>
			<input
				data-testid='checkbox-component'
				type='checkbox'
				checked={checkedFilters}
				onChange={handleDispatch}
				id={checkBoxDOMRef}
				style={{ display: 'none' }}
			/>

			<div className='label'>{state.checkProperties.label}</div>

			{checkedFilters && <span className='tick-selected'>✓</span>}

			{!hideTooltip && (
				<LabelForms
					text=''
					icon={<i className='ri-information-line' />}
					toolTipText={toolTipText}
					toolTipPosition='right'
					classname={`${checkedFilters ? 'selected' : ''}`}
				/>
			)}
		</div>
	);
	const renderStandardFilter = () => (
		<div
			className={`check-container ${isDropdownFilter ? 'dropdown-checkbox' : ''} ${
				isFirstLevelFilter ? 'first-level-checkbox' : ''
			}${isThirdLevelFilter ? 'third-level-filter' : ''}${
				isNonCategoryFilter ? 'category-checkbox' : ''
			}`}
			ref={checkBoxContainerRef}
		>
			{!isDropdownFilter && (
				<button type='button' className='check-icon-button' onClick={toggleCheckbox}>
					<i
						className={`
      check-icon ri ${iconClass}
      ${checkedFilters && !validateIndeterminate() ? 'checked' : ''}
      ${validateIndeterminate() ? 'indeterminate' : ''}
    `}
					/>
				</button>
			)}

			<input
				data-testid='checkbox-component'
				type='checkbox'
				value={state.checkProperties.label}
				checked={checkedFilters}
				onChange={handleDispatch}
				id={checkBoxDOMRef}
			/>

			<label
				style={{
					color: label === 'Expo West 2026' ? 'rgba(193, 64, 40, 1)' : 'inherit'
				}}
				className={`check-label ${children ? 'parent-label' : ''}${
					checkedFilters ? ' selected' : ''
				}
			${isDropdownFilter ? 'dropdown-check-label' : ''}
			${isFirstLevelFilter ? 'first-level-filter' : ''}
			${isLowerLevelFilter ? 'lower-level-filter' : ''}
			`}
				htmlFor={checkBoxDOMRef}
			>
				{!isNonCategoryFilter
					? toTitleCase(state.checkProperties.label ?? '')
					: state.checkProperties.label ?? ''}
			</label>

			{checkedFilters && isDropdownFilter && <span className='tick-selected'>✓</span>}
		</div>
	);

	if (isNewFilter) {
		return renderNewFilter();
	}

	return renderStandardFilter();
};
